import type { ReferralProduct } from "../../../Redux/RefProduct/types";

export interface RefProductCardData {
  id: string;
  productId: string;
  ssin: string;
  name: string;
  description: string;
  image: string;
  brandName: string;
  price: number;
  mrp: number;
  discountPercent: number;
  savingsAmount: number;
  rating: number;
  reviewCount: number;
  colors: string[];
  freeDelivery: boolean;
  fastDelivery: boolean;
  nextDayDelivery: boolean;
  stockStatus: string;
  isSponsored: boolean;
  dealLabel?: string;
  couponLabel?: string;
  voucherAmount?: number;
  category?: string;
  superCategory?: string;
  rawProduct: ReferralProduct;
}

export type RefTabId =
  | "all"
  | "todayDeals"
  | "limitedDeals"
  | "bestSeller"
  | "salarAura"
  | "refurbished";

export const REF_TABS: { id: RefTabId; label: string; tag: string }[] = [
  { id: "all", label: "All Products", tag: "" },
  { id: "todayDeals", label: "Today's Deals", tag: "Today Deals" },
  { id: "limitedDeals", label: "Limited Deals", tag: "Limited Deals" },
  { id: "bestSeller", label: "Best Seller", tag: "best seller" },
  { id: "salarAura", label: "Salar Aura", tag: "Salar Aura" },
  { id: "refurbished", label: "Refurbished", tag: "Refurbished" },
];

export type RefSort =
  | "discount"
  | "rating"
  | "price_asc"
  | "price_desc"
  | "newest";

export const SORT_OPTIONS: { value: RefSort; label: string }[] = [
  { value: "discount", label: "Discount: High to Low" },
  { value: "rating", label: "Customer Rating" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "newest", label: "Newest" },
];

/** Format a price for Indian locale (e.g. 1549 → "1,549") */
export const formatPrice = (value: number): string =>
  new Intl.NumberFormat("en-IN").format(Math.max(0, Math.round(value)));

/**
 * Map a ReferralProduct to RefProductCardData for rendering in the grid
 */
export function mapReferralProductToCard(p: ReferralProduct): RefProductCardData {
  const images =
    p.images && p.images.length > 0
      ? p.images
      : ["/assets/images/logos/salarlogo.png"];
  const unitPrice = Number(p.priceDetails?.unitPrice ?? 0);
  const finalPrice = Number(p.priceDetails?.finalPrice ?? unitPrice);
  const mrp = unitPrice > 0 ? unitPrice : finalPrice;
  const discountAmount = Number(
    p.priceDetails?.discountAmount ?? (mrp > finalPrice ? mrp - finalPrice : 0)
  );
  const discountPercent =
    mrp > 0 && mrp > finalPrice
      ? Math.round(((mrp - finalPrice) / mrp) * 100)
      : 0;

  const voucherAmount = p.shoppingVoucher?.amount;
  const dealLabel =
    p.tags?.[0] ||
    p.referralPlanName ||
    (discountPercent > 0 ? `${discountPercent}% OFF` : undefined);
  const couponLabel = voucherAmount
    ? `₹${formatPrice(voucherAmount)} Voucher`
    : undefined;

  return {
    id: String(p._id || p.productId),
    productId: String(p.productId || p._id),
    ssin: p.ssin || "",
    name: p.productName || "Referral Product",
    description:
      p.productDescription || (p.aboutThisItem && p.aboutThisItem[0]) || "",
    image: images[0],
    brandName: p.brandName || "Salar Store",
    price: finalPrice,
    mrp,
    discountPercent,
    savingsAmount: discountAmount,
    rating: (p as any).reviews?.averageRating || 0,
    reviewCount:
      (p as any).reviews?.totalReviews || (p as any).totalPurchases || 0,
    colors: [],
    freeDelivery: true,
    fastDelivery: false,
    nextDayDelivery: false,
    stockStatus: p.status === "inactive" ? "out_of_stock" : "in_stock",
    isSponsored: false,
    dealLabel,
    couponLabel,
    voucherAmount,
    category: p.categoryDetails?.category,
    superCategory: p.categoryDetails?.superCategory,
    rawProduct: p,
  };
}
